import { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { useParams, useNavigate } from "react-router-dom";
import {
  APPROVE_SUBMISSION,
  REJECT_SUBMISSION,
  CANCEL_PARTICIPATION,
} from "../../api/review";
import { GET_PARTICIPATION_DETAIL } from "../../../../campaigns/api/participation";

type Submission = {
  id: number;
  participationId: number;
  missionStepId: number;
  stepOrder: number;
  windowStartAt: string;
  windowEndAt: string;
  isSubmitted: boolean;
  isApproved: boolean;
  status: string;
  submittedAt: string | null;
  reviewedAt: string | null;
  approvedAt: string | null;
  rejectedAt: string | null;
  reviewerAccountId: number | null;
  proofType: string;
  proofData: unknown;
  note: string | null;
  step: {
    id: number;
    stepOrder: number;
    title: string | null;
    guide: unknown;
    requirements: unknown;
    notices: unknown;
    proofType: string;
    isRequired: boolean;
  };
};

const SubmissionDetail = () => {
  const { participationId } = useParams<{ participationId: string }>();
  const navigate = useNavigate();
  const [rejectNotes, setRejectNotes] = useState<Record<number, string>>({});
  const [showRejectModals, setShowRejectModals] = useState<Record<number, boolean>>({});
  const [reviewerNotes, setReviewerNotes] = useState<Record<number, string>>({});
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  const { data, loading, error, refetch } = useQuery(GET_PARTICIPATION_DETAIL, {
    variables: { participationId: parseInt(participationId || "0") },
  });

  const [approveSubmission] = useMutation(APPROVE_SUBMISSION, {
    onCompleted: () => {
      alert("승인되었습니다.");
      refetch();
    },
    onError: (error) => {
      alert(`오류: ${error.message}`);
    },
  });

  const [rejectSubmission] = useMutation(REJECT_SUBMISSION, {
    onCompleted: () => {
      alert("반려되었습니다.");
      refetch();
    },
    onError: (error) => {
      alert(`오류: ${error.message}`);
    },
  });

  const [cancelParticipation] = useMutation(CANCEL_PARTICIPATION, {
    onCompleted: (data) => {
      if (data.missionAdminCancelParticipation.ok) {
        alert("참여가 취소되었습니다. 예치금이 환불됩니다.");
        setShowCancelModal(false);
        setCancelReason("");
        refetch();
      } else {
        alert(`오류: ${data.missionAdminCancelParticipation.error || "취소에 실패했습니다."}`);
      }
    },
    onError: (error) => {
      alert(`오류: ${error.message}`);
    },
    refetchQueries: [{ query: GET_PARTICIPATION_DETAIL, variables: { participationId: parseInt(participationId || "0") } }],
  });

  const handleApprove = (submissionId: number) => {
    const participation = data?.participation;
    if (!participation) return;

    const submissions = (participation.submissions || []) as Submission[];
    const submission = submissions.find((s) => s.id === submissionId);
    const now = new Date();
    const isExpired = submission && new Date(submission.windowEndAt) < now;

    if (isExpired) {
      const expiredDate = formatDate(submission!.windowEndAt);
      const confirmMessage = `⚠️ 경고: 이 미션의 승인 기간이 지났습니다.\n\n승인 기간: ~ ${expiredDate}\n현재 시각: ${formatDate(now.toISOString())}\n\n예외적으로 승인하시겠습니까?`;
      if (!window.confirm(confirmMessage)) {
        return;
      }
    } else {
      if (!window.confirm("이 미션 제출을 승인하시겠습니까?")) {
        return;
      }
    }

    approveSubmission({
      variables: {
        submissionId,
        note: reviewerNotes[submissionId] || null,
      },
    });
  };

  const handleReject = (submissionId: number) => {
    const rejectNote = rejectNotes[submissionId];
    if (!rejectNote?.trim()) {
      alert("반려 사유를 입력해주세요.");
      return;
    }

    rejectSubmission({
      variables: {
        submissionId,
        note: rejectNote,
      },
    });

    setShowRejectModals((prev) => ({ ...prev, [submissionId]: false }));
    setRejectNotes((prev) => ({ ...prev, [submissionId]: "" }));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDateOnly = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  // proofData에서 imageUrl 배열 추출하는 헬퍼 함수
  const extractImageUrls = (proofData: unknown): string[] => {
    if (!proofData) return [];

    // 1. 문자열인 경우 JSON 파싱 시도
    if (typeof proofData === "string") {
      try {
        const parsed = JSON.parse(proofData);
        return extractImageUrls(parsed); // 재귀적으로 파싱된 값 처리
      } catch {
        // JSON이 아니면 그냥 URL 문자열로 간주
        return [proofData];
      }
    }

    // 2. 배열인 경우
    if (Array.isArray(proofData)) {
      const urls: string[] = [];
      proofData.forEach((item) => {
        const extracted = extractImageUrls(item);
        urls.push(...extracted);
      });
      return urls;
    }

    // 3. 객체인 경우
    if (proofData && typeof proofData === "object" && proofData !== null) {
      const obj = proofData as Record<string, unknown>;
      if (typeof obj.imageUrl === "string") {
        return [obj.imageUrl];
      }
    }

    return [];
  };

  const renderProofData = (proofType: string, proofData: unknown) => {
    if (!proofData) {
      return <p className="text-gray-500">제출 내용이 없습니다.</p>;
    }

    switch (proofType) {
      // 서버 ProofType: NONE | SCREENSHOT | PHOTO | TEXT | URL
      case "PHOTO":
      case "SCREENSHOT": {
        const images = extractImageUrls(proofData);

        if (images.length === 0) {
          return <p className="text-gray-500">제출 내용이 없습니다.</p>;
        }

        return (
          <div className="space-y-4">
            {images.map((img, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg p-2">
                <img
                  src={img}
                  alt={`제출 이미지 ${idx + 1}`}
                  className="max-w-full h-auto rounded cursor-pointer"
                  onClick={() => window.open(img, "_blank")}
                  onError={(e) => {
                    // 이미지 로드 실패 시 에러 표시
                    const target = e.currentTarget;
                    target.style.display = "none";
                    const parent = target.parentElement;
                    if (parent) {
                      parent.innerHTML = '<p class="text-red-500 text-sm">이미지를 불러올 수 없습니다.</p>';
                    }
                  }}
                />
              </div>
            ))}
          </div>
        );
      }
      case "TEXT":
        return (
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="whitespace-pre-wrap">{String(proofData)}</p>
          </div>
        );
      case "URL":
        return (
          <div>
            <a
              href={String(proofData)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-700 underline"
            >
              {String(proofData)}
            </a>
          </div>
        );
      case "NONE":
      default:
        return <p className="text-gray-500">지원하지 않는 증빙 타입입니다.</p>;
    }
  };

  if (loading) return <div className="text-center py-8">로딩 중...</div>;
  if (error)
    return (
      <div className="text-center py-8 text-red-600">에러: {error.message}</div>
    );

  const participation = data?.participation;
  if (!participation) {
    return (
      <div className="text-center py-8">참여 정보를 찾을 수 없습니다.</div>
    );
  }

  const campaign = participation.run.campaign;
  const submissions = (participation.submissions || []) as Submission[];
  const sortedSubmissions = [...submissions].sort((a, b) => a.stepOrder - b.stepOrder);
  const steps = campaign.steps || [];
  const sortedSteps = [...steps].sort((a, b) => a.stepOrder - b.stepOrder);

  // 회차 정보 계산
  const runs = campaign.runs || [];
  const sortedRuns = [...runs].sort((a, b) => 
    new Date(a.startAt).getTime() - new Date(b.startAt).getTime()
  );
  const currentRunIndex = sortedRuns.findIndex(run => run.id === participation.run.id);
  const runNumber = currentRunIndex >= 0 ? currentRunIndex + 1 : null;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate("/ops/mission/reviews/submissions")}
          className="text-blue-600 hover:text-blue-700"
        >
          ← 목록으로
        </button>
        <h1 className="text-2xl font-bold">미션 제출 검수</h1>
        <div></div>
      </div>

      {/* 유저 및 캠페인 정보 */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-500">유저</label>
            <p className="text-base font-medium">
              {participation.user?.name || `유저 ${participation.userId}`} (ID: {participation.userId})
            </p>
            {participation.user?.phone && (
              <p className="text-sm text-gray-500 mt-1">
                {participation.user.phone}
              </p>
            )}
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">캠페인</label>
            <div className="flex items-center gap-3 mt-1">
              {campaign.productImageUrl && (
                <img
                  src={campaign.productImageUrl}
                  alt={campaign.productName}
                  className="w-12 h-12 object-cover rounded"
                />
              )}
              <div>
                <p className="text-base font-medium">{campaign.productName}</p>
                <p className="text-sm text-gray-500">{campaign.brand.name}</p>
                {runNumber && participation.run.startAt && participation.run.endAt && (
                  <p className="text-sm text-blue-600 mt-1">
                    회차: {runNumber}회 ({formatDateOnly(participation.run.startAt)} ~ {formatDateOnly(participation.run.endAt)})
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 모든 미션 제출 목록 */}
      <div className="space-y-6">
        {sortedSteps.map((step) => {
          const submission = sortedSubmissions.find((s) => s.stepOrder === step.stepOrder);
          
          if (!submission) {
            return (
              <div key={step.id} className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">
                  {step.title || `미션 ${step.stepOrder}`}
                </h3>
                <div className="text-center py-8 text-gray-500">
                  아직 제출되지 않았습니다.
                </div>
              </div>
            );
          }

          const isSubmitted = submission.status === "SUBMITTED";
          const isApproved = submission.status === "APPROVED";
          const isRejected = submission.status === "REJECTED";
          const now = new Date();
          const isExpired = new Date(submission.windowEndAt) < now;

          return (
            <div key={submission.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">
                  {step.title || `미션 ${step.stepOrder}`}
                </h3>
                <div>
                  {isSubmitted && (
                    <span className="px-3 py-1 text-sm font-medium rounded bg-yellow-100 text-yellow-800">
                      검수 대기
                    </span>
                  )}
                  {isApproved && (
                    <span className="px-3 py-1 text-sm font-medium rounded bg-green-100 text-green-800">
                      승인됨
                    </span>
                  )}
                  {isRejected && (
                    <span className="px-3 py-1 text-sm font-medium rounded bg-red-100 text-red-800">
                      반려됨
                    </span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* 제출 내용 */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">제출 내용</h4>
                  {submission.proofData ? (
                    renderProofData(submission.proofType, submission.proofData)
                  ) : (
                    <p className="text-gray-500">제출 내용이 없습니다.</p>
                  )}
                  {submission.note && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-500 mb-2">유저 메모</h4>
                      <p className="text-sm bg-gray-50 p-3 rounded">{submission.note}</p>
                    </div>
                  )}
                  {submission.submittedAt && (
                    <div className="mt-4">
                      <p className="text-xs text-gray-500">
                        제출 일시: {formatDate(submission.submittedAt)}
                      </p>
                    </div>
                  )}
                </div>

                {/* 미션 요구사항 및 검수 액션 */}
                <div className="space-y-4">
                  {/* 미션 요구사항 */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">미션 요구사항</h4>
                    <div className="space-y-2 text-sm">
                      {step.guide && Array.isArray(step.guide) && step.guide.length > 0 && (
                        <div>
                          <p className="font-medium mb-1">가이드:</p>
                          <ul className="list-disc list-inside space-y-1 text-gray-600">
                            {step.guide.map((item: unknown, idx: number) => (
                              <li key={idx}>{String(item)}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {step.requirements && Array.isArray(step.requirements) && step.requirements.length > 0 && (
                        <div>
                          <p className="font-medium mb-1">요구사항:</p>
                          <ul className="list-disc list-inside space-y-1 text-gray-600">
                            {step.requirements.map((item: unknown, idx: number) => (
                              <li key={idx}>{String(item)}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {step.notices && Array.isArray(step.notices) && step.notices.length > 0 && (
                        <div>
                          <p className="font-medium mb-1">주의사항:</p>
                          <ul className="list-disc list-inside space-y-1 text-gray-600">
                            {step.notices.map((item: unknown, idx: number) => (
                              <li key={idx}>{String(item)}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 검수 액션 */}
                  {isSubmitted && (
                    <div className="border-t border-gray-200 pt-4">
                      <div className="space-y-3">
                        {isExpired && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                            <div className="flex items-start gap-2">
                              <span className="text-yellow-600 text-lg">⚠️</span>
                              <div className="flex-1">
                                <p className="text-sm font-medium text-yellow-800 mb-1">
                                  승인 기간이 지났습니다
                                </p>
                                <p className="text-xs text-yellow-700">
                                  승인 기간: ~ {formatDate(submission.windowEndAt)}
                                </p>
                                <p className="text-xs text-yellow-700 mt-1">
                                  예외적으로 승인할 수 있습니다.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            검수자 메모 (선택)
                          </label>
                          <textarea
                            value={reviewerNotes[submission.id] || ""}
                            onChange={(e) =>
                              setReviewerNotes((prev) => ({
                                ...prev,
                                [submission.id]: e.target.value,
                              }))
                            }
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            rows={2}
                            placeholder="검수자 메모를 입력하세요"
                          />
                        </div>
                        <div className="flex gap-3">
                          <button
                            onClick={() => handleApprove(submission.id)}
                            className={`flex-1 rounded-lg py-2 px-4 text-sm font-semibold ${
                              isExpired
                                ? "bg-yellow-600 text-white hover:bg-yellow-700"
                                : "bg-green-600 text-white hover:bg-green-700"
                            }`}
                          >
                            {isExpired ? "⚠️ 예외 승인" : "승인"}
                          </button>
                          <button
                            onClick={() =>
                              setShowRejectModals((prev) => ({
                                ...prev,
                                [submission.id]: true,
                              }))
                            }
                            className="flex-1 bg-red-600 text-white rounded-lg py-2 px-4 text-sm font-semibold hover:bg-red-700"
                          >
                            반려
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 검수 완료 상태 */}
                  {(isApproved || isRejected) && (
                    <div className="border-t border-gray-200 pt-4">
                      <div className="space-y-2 text-sm">
                        {submission.reviewedAt && (
                          <div>
                            <span className="font-medium text-gray-500">검수 일시: </span>
                            <span>{formatDate(submission.reviewedAt)}</span>
                          </div>
                        )}
                        {submission.note && (
                          <div>
                            <span className="font-medium text-gray-500">검수 메모: </span>
                            <p className="mt-1 bg-gray-50 p-2 rounded text-gray-700">
                              {submission.note}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* 반려 모달 */}
              {showRejectModals[submission.id] && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                    <h3 className="text-lg font-semibold mb-4">반려 사유 입력</h3>
                    <textarea
                      value={rejectNotes[submission.id] || ""}
                      onChange={(e) =>
                        setRejectNotes((prev) => ({
                          ...prev,
                          [submission.id]: e.target.value,
                        }))
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-4"
                      rows={4}
                      placeholder="반려 사유를 입력하세요"
                    />
                    <div className="flex gap-3">
                      <button
                        onClick={() =>
                          setShowRejectModals((prev) => ({
                            ...prev,
                            [submission.id]: false,
                          }))
                        }
                        className="flex-1 border border-gray-300 rounded-lg py-2 font-semibold text-gray-700"
                      >
                        취소
                      </button>
                      <button
                        onClick={() => handleReject(submission.id)}
                        disabled={!rejectNotes[submission.id]?.trim()}
                        className="flex-1 bg-red-600 text-white rounded-lg py-2 font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        반려
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* 참여 취소 모달 */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">참여 취소</h3>
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                참여를 취소하면 예치금이 환불됩니다. 정말 취소하시겠습니까?
              </p>
              <p className="text-xs text-red-600 mb-4">
                ⚠️ 취소 후에는 되돌릴 수 없습니다.
              </p>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                취소 사유 <span className="text-red-500">*</span>
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                rows={4}
                placeholder="취소 사유를 입력하세요 (필수)"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelReason("");
                }}
                className="flex-1 border border-gray-300 rounded-lg py-2 font-semibold text-gray-700 hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={() => {
                  if (!cancelReason.trim()) {
                    alert("취소 사유를 입력해주세요.");
                    return;
                  }
                  cancelParticipation({
                    variables: {
                      participationId: parseInt(participationId || "0"),
                      cancelReason: cancelReason.trim(),
                    },
                  });
                }}
                disabled={!cancelReason.trim()}
                className="flex-1 bg-red-600 text-white rounded-lg py-2 font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubmissionDetail;
